import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';

import ErrorBoundary from 'components/Common/ErrorBoundary';
import Skeleton from 'components/Skeleton';
import BaseViewsContext from 'contexts/BaseViewsContext';
import CardContext from 'contexts/CardContext';
import DashboardGadgetContext from 'contexts/DashboardGadgetContext';
import useMetric from 'hooks/useMetric';
import usePopupCallback from 'hooks/usePopupCallback';

import { isEntityDetailsTable } from './buildEntityInformationTableDefinition';
import { isGroupVsRemainderLine } from './buildGroupVsRemainderDefinition';
import { isLegacyMatrixTable } from './buildLegacyMatrixDefinition';
import { isMetricListTable } from './buildMetricListDefinition';
import { isRemindersTable } from './buildRemindersDefinition';
import { isRecordsTable } from './buildTableDefinition';
import { isKpiListTable, useKpiListScorecard } from './kpiList';
import { buildGadgetChartDefinition } from './canvasMapping/chartMatching';
import { DEFAULT_VIS_TYPE, getVisType } from './visTypes';
import {
  CardActions,
  CardHeader,
  CardShell,
  OverlayActions,
  OverlayViewMenu,
  SHELL_PADDING,
} from './CardChrome';
import ComparisonBadge from './ComparisonBadge';
import { getCardMetricIds, getRatioPercent } from './compositeUtils';
import { DEFAULT_ACCENT, toAccentColor, toAccentCss } from './constants';
import { RatioCaption, RatioDenominator } from './NumberRatio';
import NumberSubtext from './NumberSubtext';
import ProgressBar from './ProgressBar';
import BlockMenuItems, {
  BlockMenuScope,
  hasBlockMenuItems,
} from './BlockMenuItems';
import { getLegacyGadgetBlockName } from './legacyGadgetBlocks';
import StudioChart from './StudioChart';
import { useStudioChartDrillDown } from './StudioChartMenu';
import StudioEntityDetails from './StudioEntityDetails';
import StudioGauge from './StudioGauge';
import StudioGroupVsRemainder from './StudioGroupVsRemainder';
import StudioKpiList from './StudioKpiList';
import StudioLegacyMatrix from './StudioLegacyMatrix';
import StudioMetricList from './StudioMetricList';
import StudioReminders from './StudioReminders';
import { getResolvedMetricIds } from './seriesVariants';
import SetupPlaceholder, { SetupBanner, SetupFrame } from './SetupPlaceholder';
import getSetupSteps, { isSetUp } from './setupSteps';
import { toRowKey, useRowFontSize } from './NumberRowFontSizeContext';
import useFittedFontSize from './useFittedFontSize';
import useRenderedComparison from './useRenderedComparison';
import useStudioMetricValue from './useStudioMetricValue';

// Inset so the value and its captions stay off the block's border
const ValueWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 0;
  padding: 0 ${SHELL_PADDING.x}px ${SHELL_PADDING.y}px;
  text-align: center;
`;

const Value = styled.div<{ $fontSize: number }>`
  font-size: ${(props) => props.$fontSize}px;
  font-weight: 600;
  line-height: 1.1;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

// Fills the body below the header, so the whole block opens its popup
const PopupArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  cursor: pointer;
`;

// On a dashboard, a Number block with a popup report or board opens it from
// anywhere on its body, not just the header's hover-only button
const NumberPopupArea = ({ children }: { children: JSX.Element }) => {
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  const openPopup = usePopupCallback(dashboardGadget);
  if (!dashboardGadget || !openPopup) {
    return children;
  }

  return (
    <PopupArea
      role="button"
      onClick={() => openPopup()}
      data-testid={`studio-card-popup-${dashboardGadget.id}`}
    >
      {children}
    </PopupArea>
  );
};

// The gadget a Number block saves as (a card, or a smart card when compared),
// as the CardContext its values are fetched in. The date scope depends on it
// as on the dashboard: e.g. last year from the same weekday for a smart card
// over a rolling window, or a run-time date filter narrowing the range. Kept
// while its contents don't change (e.g. the block only moves), as the
// fetches follow its identity.
const GadgetScope = ({
  card,
  name,
  children,
}: {
  card: Studio.Card;
  name: string;
  children: JSX.Element;
}) => {
  const { w, h } = card.layout;
  const built = buildGadgetChartDefinition({ card, id: card.id, name });
  const chartDefinitionKey =
    'chartDefinition' in built ? JSON.stringify(built.chartDefinition) : '';
  const cardContextValue = useMemo(
    () => ({
      i: card.id,
      w,
      h,
      chartDefinition: chartDefinitionKey
        ? (JSON.parse(chartDefinitionKey) as V5ChartDefinition)
        : undefined,
    }),
    [card.id, chartDefinitionKey, h, w],
  );
  return (
    <CardContext.Provider value={cardContextValue}>
      {children}
    </CardContext.Provider>
  );
};

// A Number block's value (its first metric) and its captions. Its other
// metrics are only shown through the subtext's placeholders.
const NumberBody = ({
  card,
  valueFontSize,
  isOnView,
}: {
  card: Studio.Card;
  valueFontSize: number;
  isOnView: boolean;
}) => {
  const metricIds = useMemo(() => card.metricIds ?? [], [card.metricIds]);
  const primaryMetric = useMetric(metricIds[0]);
  const { value, formattedValue, isLoading, isError } = useStudioMetricValue({
    metric: primaryMetric,
  });
  const comparison = useRenderedComparison({ content: card, isOnView });
  // Every metric the block shows, which its subtext can name
  const blockMetricIds = useMemo(() => getCardMetricIds(card), [card]);

  // Shown out of another metric: "137 / 230", its share and a bar
  const denominatorMetric = useMetric(card.denominatorMetricId);
  const denominator = useStudioMetricValue({ metric: denominatorMetric });
  const ratio = denominatorMetric
    ? getRatioPercent({ numerator: value, denominator: denominator.value })
    : undefined;

  // The block's size gives the largest the value can be; it shrinks to fit
  // alongside its captions, and to the smallest beside it in its row
  const {
    fontSize: fittedFontSize,
    wrapperRef,
    valueRef,
  } = useFittedFontSize({
    maxFontSize: valueFontSize,
    fitKey: [
      isLoading,
      formattedValue,
      denominator.formattedValue,
      !!comparison,
      !!card.showProgressBar,
      card.subtext,
      card.denominatorLabel,
    ].join('|'),
  });
  const fontSize = useRowFontSize({
    cardId: card.id,
    rowKey: toRowKey(card.layout),
    fontSize: fittedFontSize,
  });

  return (
    <ValueWrapper ref={wrapperRef}>
      {isLoading && primaryMetric ? (
        <Skeleton width="50%" height={fontSize} />
      ) : (
        <Value
          ref={valueRef}
          $fontSize={fontSize}
          data-testid={`studio-card-value-${card.id}`}
        >
          {isError ? '!' : formattedValue}
          {denominatorMetric && (
            <RatioDenominator data-testid={`studio-card-ratio-${card.id}`}>
              {' / '}
              {denominator.isLoading ? '…' : denominator.formattedValue}
            </RatioDenominator>
          )}
        </Value>
      )}
      {primaryMetric && comparison && (
        <ComparisonBadge
          metric={primaryMetric}
          current={value}
          comparison={comparison}
          show={card.comparison?.show}
          isCaptionHidden={card.comparison?.compareType === 'report'}
          testId={`studio-card-delta-${card.id}`}
        />
      )}
      {denominatorMetric && card.showProgressBar && (
        <ProgressBar
          percent={ratio}
          color={toAccentCss(DEFAULT_ACCENT)}
          testId={`studio-card-progress-${card.id}`}
        />
      )}
      {denominatorMetric && ratio !== undefined && (
        <RatioCaption
          ratio={ratio}
          label={card.denominatorLabel || denominatorMetric.name}
          testId={`studio-card-ratio-caption-${card.id}`}
        />
      )}
      <NumberSubtext
        text={card.subtext}
        metricIds={blockMetricIds}
        testId={`studio-card-subtext-${card.id}`}
      />
    </ValueWrapper>
  );
};

const StudioCard = ({
  card,
  isSelected,
  isDropTarget,
  valueFontSize,
  actions,
  viewMenu,
  isEmbedded = false,
}: {
  card: Studio.Card;
  isSelected: boolean;
  isDropTarget: boolean;
  valueFontSize: number;
  // Absent in fullscreen, where the block is view-only
  actions?: CardActions;
  // On a dashboard or report (isEmbedded): the gadget's menu buttons, in the
  // header or floated over the corner when it's hidden
  viewMenu?: JSX.Element;
  isEmbedded?: boolean;
}) => {
  const primaryMetric = useMetric(getResolvedMetricIds(card)[0]);
  const { getDatasetLabel } = useContext(BaseViewsContext);
  const drillDownMenu = useStudioChartDrillDown({ content: card, actions });
  const isNumber = getVisType(card.visType).id === DEFAULT_VIS_TYPE;
  // A records Table is named after the dataset it lists
  const recordsDataType = isRecordsTable(card)
    ? card.table?.records?.dataType
    : undefined;
  // A KPI list is titled by its KPI List, in a header of its own (as the
  // classic Scorecard card), so the block's header floats instead
  const { scorecard } = useKpiListScorecard(card);
  const isKpiList = isKpiListTable(card);
  const isHeaderHidden = card.isHeaderHidden || isKpiList;
  const setupSteps = getSetupSteps(card);
  // In Studio, a chart that can't draw yet shows what it'll look like
  const isBeingSetUp = !isEmbedded && !isSetUp(setupSteps);

  const title =
    card.name ||
    (scorecard && scorecard.title) ||
    getLegacyGadgetBlockName(card) ||
    (isRecordsTable(card)
      ? recordsDataType
        ? getDatasetLabel(recordsDataType)
        : 'Records'
      : primaryMetric
        ? primaryMetric.name
        : 'Unknown metric');
  // In Studio, the block's own options (e.g. a Table's export) join its
  // actions; on a dashboard or report, its view menu has them
  const extraMenu =
    actions && hasBlockMenuItems({ content: card, isOnView: false }) ? (
      <BlockMenuItems
        content={card}
        name={title}
        w={card.layout.w}
        h={card.layout.h}
      />
    ) : undefined;

  const numberBody = (
    <NumberBody
      card={card}
      valueFontSize={valueFontSize}
      isOnView={isEmbedded}
    />
  );

  const shell = (
    <CardShell
      $isSelected={isSelected || isDropTarget}
      $isActive={isSelected}
      $isEmbedded={isEmbedded}
      data-testid={`studio-card-${card.id}`}
    >
      {isHeaderHidden ? (
        actions ? (
          <OverlayActions
            cardId={card.id}
            actions={actions}
            dates={{
              hasOwnDates: !!card.dateSettings,
              hasFilters: (card.filters ?? []).length > 0,
            }}
            extraMenu={extraMenu}
          />
        ) : (
          viewMenu && (
            <OverlayViewMenu cardId={card.id}>{viewMenu}</OverlayViewMenu>
          )
        )
      ) : (
        <CardHeader
          cardId={card.id}
          title={title}
          description={card.isDescriptionShown ? card.description : undefined}
          icon={
            card.headerIcon && {
              name: card.headerIcon,
              // Plain unless an accent is chosen
              color:
                (card.accent && toAccentColor(card.accent)) ||
                'var(--color-icon-secondary)',
            }
          }
          hasOwnDates={!!card.dateSettings}
          hasFilters={(card.filters ?? []).length > 0}
          actions={actions}
          viewMenu={viewMenu}
          extraMenu={extraMenu}
        />
      )}
      {/* A body that fails to draw shows the classic gadget's error, with
          Retry (as CanvasCard does), under a header whose menu still works */}
      <ErrorBoundary isRetryable>
        <>
          {isBeingSetUp ? (
            <SetupPlaceholder
              visType={card.visType}
              // Only on the canvas (with actions), where it can be selected
              isHintShown={!isSelected && !!actions}
              isHeaderHidden={isHeaderHidden}
              testId={`studio-card-missing-${card.id}`}
            />
          ) : card.visType === 'gauge' ? (
            <StudioGauge
              content={card}
              name={title}
              isInteractive={isSelected || !actions}
            />
          ) : isLegacyMatrixTable(card) ? (
            <StudioLegacyMatrix
              content={card}
              name={title}
              w={card.layout.w}
              h={card.layout.h}
              isInteractive={isSelected || !actions}
              isOnView={isEmbedded}
            />
          ) : isKpiList ? (
            <StudioKpiList
              content={card}
              isInteractive={isSelected || !actions}
              isOnView={isEmbedded}
            />
          ) : isMetricListTable(card) ? (
            <StudioMetricList
              content={card}
              name={title}
              isInteractive={isSelected || !actions}
            />
          ) : isRemindersTable(card) ? (
            <StudioReminders
              content={card}
              name={title}
              w={card.layout.w}
              isInteractive={isSelected || !actions}
              isOnView={isEmbedded}
            />
          ) : isEntityDetailsTable(card) ? (
            <StudioEntityDetails
              content={card}
              name={title}
              w={card.layout.w}
              h={card.layout.h}
              isInteractive={isSelected || !actions}
              isOnView={isEmbedded}
            />
          ) : isGroupVsRemainderLine(card) ? (
            <StudioGroupVsRemainder
              content={card}
              name={title}
              w={card.layout.w}
              h={card.layout.h}
              isInteractive={isSelected || !actions}
            />
          ) : isNumber ? (
            <GadgetScope card={card} name={title}>
              {/* Not in fullscreen (no view menu), which a popup would sit
                  under */}
              {isEmbedded && viewMenu ? (
                <NumberPopupArea>{numberBody}</NumberPopupArea>
              ) : (
                numberBody
              )}
            </GadgetScope>
          ) : (
            <StudioChart
              content={card}
              name={title}
              w={card.layout.w}
              h={card.layout.h}
              // Fullscreen (and dashboard) charts always take the pointer
              isInteractive={isSelected || !actions}
              isOnView={isEmbedded}
              drillDownMenu={drillDownMenu}
            />
          )}
        </>
      </ErrorBoundary>
    </CardShell>
  );

  return (
    <BlockMenuScope content={card}>
      {isEmbedded ? (
        shell
      ) : (
        // Selected, a chart still being set up lists what it needs beneath
        <SetupFrame>
          {shell}
          {isBeingSetUp && isSelected && (
            <SetupBanner
              visType={card.visType}
              steps={setupSteps}
              onStepClick={actions ? actions.onOpenSettings : undefined}
            />
          )}
        </SetupFrame>
      )}
    </BlockMenuScope>
  );
};

export default StudioCard;
